import { useCallback, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  cartIdSelector,
  emailSchema,
  resolveCatchMessage,
  TCatchMessage,
  TEmailSchema,
  useLoadingContext,
  userIsLoggedInSelector,
  useToastContext,
} from '@segway/core'
import { useSetGuestEmailOnCartMutation } from '@segway/core/src/services/client/checkoutApi'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useForm } from 'react-hook-form'

/**
 * 结算页联系信息 Hooks
 */
const useContactInformation = () => {
  const toast = useToastContext()
  const form = useForm<TEmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const isLoggedIn = useAppSelector(userIsLoggedInSelector)
  const loading = useLoadingContext()
  const cartId = useAppSelector(cartIdSelector)
  const [setGuestEmailOnCartMutation, { isLoading }] = useSetGuestEmailOnCartMutation()

  const [curEmail, setCurEmail] = useState<string>('')
  const [isEdit, setIsEdit] = useState<boolean>(true)
  const [isSubscribe, setIsSubscribe] = useState<boolean>(false)

  /**
   * 编辑游客邮箱 - 显示输入框
   */
  const handleEditEmail = useCallback(() => {
    setIsEdit(true)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)
  }, [])

  /**
   * 选择/取消订阅邮箱
   */
  const onSubscribeChange = useCallback((isChecked: boolean) => {
    setIsSubscribe(isChecked)
  }, [])

  /**
   * 保存游客邮箱
   */
  const onSubmit = useCallback(
    async ({ email }: TEmailSchema) => {
      try {
        if (email === curEmail) {
          setIsEdit(false)
          return
        }
        loading.show()
        await setGuestEmailOnCartMutation({
          input: {
            cart_id: cartId,
            email,
          },
        }).unwrap()
        setCurEmail(email)
        setIsEdit(false)
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [curEmail, loading, setGuestEmailOnCartMutation, cartId, toast],
  )

  return {
    isLoggedIn,
    isLoading,
    form,
    onSubmit,
    curEmail,
    isEdit,
    handleEditEmail,
    inputRef,
    isSubscribe,
    onSubscribeChange,
  }
}

export default useContactInformation
